import { classifyJobCompletion } from './job-completion.js';
import { mergeRequestBatches } from './generation-retry.js';
import { isImageDeliveryUnverified, isSdkLocalImageTimeout } from './image-delivery-status.js';
import { getGenerationErrorDetails } from './generation-error-details.js';

export function unidentifiedSubmissions(run) {
  return (run?.request?.slots || []).filter((slot) => {
    const result = run.results?.[slot.id];
    return result?.unverified && !result.projectId;
  });
}

/** Match the exact saved attempt lineage, never a prompt, model, seed or ordering. */
export function recoveredSlotProjects(run, synced) {
  const pending = new Map();
  for (const slot of unidentifiedSubmissions(run)) {
    const lineage = slot.projectConfig?.attribution;
    // The earlier slot-only lineage was reused on retries and cannot prove
    // which attempt a project belongs to, even when only one project remains.
    if (lineage?.rootOperationId !== run.request.id || !lineage.parentOperationId || lineage.parentOperationId === slot.id) continue;
    pending.set(lineage.parentOperationId, slot.id);
  }
  if (!pending.size) return [];
  const candidates = new Map();
  const recovered = [...(synced.snapshot?.activeProjects || []),
    ...(synced.snapshot?.unclaimedCompletedProjects || [])];
  for (const project of recovered) {
    if (!project.id || typeof project.clientRequestData !== 'string') continue;
    let request;
    try {
      const bytes = Uint8Array.from(atob(project.clientRequestData), (character) => character.charCodeAt(0));
      request = JSON.parse(new TextDecoder().decode(bytes));
    } catch { continue; }
    if (request?.rootOperationId !== run.request.id || !pending.has(request.parentOperationId)) continue;
    const slotId = pending.get(request.parentOperationId);
    const ids = candidates.get(slotId) || new Set();
    ids.add(project.id);
    candidates.set(slotId, ids);
  }
  return [...candidates].filter(([, ids]) => ids.size === 1)
    .map(([slotId, ids]) => ({ slotId, projectId: [...ids][0] }));
}

export function projectsNeedingRecovery(run) {
  return [...new Set(Object.values(run?.results || {}).filter((result) => {
    if (!result?.projectId) return false;
    if (result.unverified) return true;
    if (['done', 'completed'].includes(result.status)) return !result.blob;
    return !['error', 'failed', 'canceled', 'cancelled'].includes(result.status);
  }).map((result) => result.projectId))];
}

export function projectUpdate(project, config) {
  const job = project.jobs?.[0];
  const base = { projectId: project.id, jobId: job?.id || null, progress: project.progress,
    waitingReason: project.waitingReason || null, queueStatus: project.queueStatus || null,
    estimatedStartAt: project.estimatedStartAt?.getTime?.() || null };
  if (project.status === 'completed') {
    const completed = classifyJobCompletion(job, { allowNSFWResult: config.disableNSFWFilter === true });
    if (completed.code === 'IMAGE_RESULT_MISSING') return { ...base, status: 'unverified', unverified: true,
      errorCode: 'RESULT_UNAVAILABLE', error: 'The completed image is not available to download yet. Check this existing project again.' };
    return completed.success ? { ...base, status: 'done', url: completed.resultUrl,
      seed: job.seed ?? config.seed, workerName: job.workerName || null, unverified: false,
      ...(completed.sensitive ? { sensitive: true } : {}) } :
      { ...base, status: 'error', error: completed.error, errorCode: completed.code, unverified: false };
  }
  if (['failed', 'canceled'].includes(project.status)) {
    if (isImageDeliveryUnverified(project.error, project.id)) {
      const details = getGenerationErrorDetails(project.error);
      return { ...base, status: 'unverified', unverified: true,
        errorCode: details.originalCode === 'projectLost' ? 'PROJECT_LOST' : details.code || 'PROJECT_RESULT_UNVERIFIED',
        error: 'This project could not be verified. Check existing projects or account creations before retrying.' };
    }
    return { ...base, status: project.status === 'canceled' ? 'canceled' : 'error',
      error: project.error?.message || 'Generation failed', errorCode: project.error?.code,
      // The string reason (e.g. 'sensitiveContent') decides the error kind shown.
      originalCode: project.error?.originalCode ?? null,
      errorData: project.error,
      unverified: false };
  }
  return { ...base, status: project.status === 'processing' ? 'rendering' : 'queued', unverified: false };
}

function projectResultUpdate(result) {
  const job = result.jobs?.[0];
  if (job?.url) return { status: 'done', url: job.url, jobId: job.id,
    ...(typeof job.seed === 'number' ? { seed: job.seed } : {}), unverified: false };
  const canceled = result.status === 'canceled' || job?.status === 'canceled';
  if (canceled || result.status === 'failed' || job?.status === 'failed' || job?.urlUnavailable === 'sensitiveContent') {
    return { status: canceled ? 'canceled' : 'error', error: job?.reason ||
      (job?.urlUnavailable === 'sensitiveContent' ? 'Filtered (NSFW)' : 'Generation ended while you were away'),
      ...(job?.urlUnavailable === 'sensitiveContent' ? { errorCode: 'NSFW_FILTERED' } : {}), unverified: false };
  }
  return { status: 'unverified', unverified: true,
    errorCode: result.status === 'completed' ? 'RESULT_UNAVAILABLE' : 'PROJECT_RESULT_UNVERIFIED',
    error: result.status === 'completed'
      ? 'The completed image is not available to download yet. Check this existing project again.'
      : 'This existing project is still running or awaiting verification. Check it again before retrying.' };
}

/**
 * The server's sensitive label on a raw project record (socket snapshot entry,
 * recovered-completed entry, or `GET /v1/projects/:id`). Raw records keep their
 * jobs under `completedWorkerJobs` and `workerJobs` (SDK `RawProject`), and the
 * socket writes `nsfwDetected` onto each worker job. The SDK's replay and result
 * lookups rebuild jobs without that field, so recovery reads the raw records it
 * already receives. Only the delivered-result label counts, never a content
 * rating derived from the model or LoRA.
 */
export function rawProjectSensitive(record) {
  const labelled = (jobs) => Array.isArray(jobs) && jobs.some((job) => job?.nsfwDetected === true);
  return labelled(record?.completedWorkerJobs) || labelled(record?.workerJobs);
}

/**
 * Project ids whose raw synced snapshot records carry the sensitive label
 * (`recoveredCompleted` entries are copies of `unclaimedCompletedProjects`).
 */
export function recoveredSensitiveLabels(synced) {
  const labels = new Set();
  const records = [...(synced?.snapshot?.activeProjects || []), ...(synced?.snapshot?.unclaimedCompletedProjects || [])];
  for (const record of records) {
    if (record?.id && rawProjectSensitive(record)) labels.add(record.id);
  }
  return labels;
}

/**
 * The server's label on one known project, from one read of its record. Used
 * where the SDK hands back jobs without the label: `getResult` entries and
 * completions replayed after a reconnect (which the SDK makes indistinguishable
 * from live unlabelled ones). The live lookup (`projects.getStatus`,
 * `GET /v2/projects/:id`) carries the raw job arrays and answers while the
 * socket holds the project; once it no longer does, the stored record
 * (`projects.get`, `GET /v1/projects/:id`) answers. Only a 404 falls through;
 * any other failure, or a 404 from both, is thrown for the caller to weigh.
 */
export async function lookupProjectSensitive(projects, projectId) {
  try {
    return rawProjectSensitive(await projects.getStatus(projectId));
  } catch (error) {
    if (error?.status !== 404) throw error;
    return rawProjectSensitive(await projects.get(projectId));
  }
}

/** One reconciliation of saved IDs. No new project requests or history enumeration. */
export async function recoverSavedProjects({ projects, projectIds, onSync, onProject, onUpdate, isCurrent }) {
  const wanted = new Set(projectIds);
  if ((!wanted.size && !onSync) || !isCurrent()) return;
  const synced = await projects.sync('manual');
  if (!isCurrent()) return;
  for (const id of onSync?.(synced) || []) wanted.add(id);
  const completed = new Map((synced.recoveredCompleted || []).filter((project) => wanted.has(project.id))
    .map((project) => [project.id, project]));
  for (const [id, project] of completed) {
    const url = project.resultUrls?.[0];
    onUpdate(id, url ? { status: 'done', url, unverified: false } :
      { status: 'unverified', errorCode: 'RESULT_UNAVAILABLE',
        error: 'The completed image is not available to download yet. Check this existing project again.', unverified: true });
  }
  const tracked = new Map(projects.trackedProjects.filter((project) => wanted.has(project.id))
    .map((project) => [project.id, project]));
  for (const project of tracked.values()) {
    onProject(project);
    // A locally timed-out SDK entity is terminal only in this tab. Ask the
    // server once about that same ID, as with an expired completed-result URL.
    const localTimeout = isSdkLocalImageTimeout(project.error);
    if ((project.status === 'completed' || localTimeout) && !completed.has(project.id)) {
      let result;
      try {
        result = await projects.getResult(project.id, { kind: 'image' });
      } catch (error) {
        if (!isCurrent()) return;
        if (!localTimeout) throw error;
        onUpdate(project.id, { status: 'unverified', unverified: true, errorCode: 'PROJECT_RESULT_UNVERIFIED',
          error: 'This existing project could not be verified. Check it again before retrying.' });
        continue;
      }
      if (!isCurrent()) return;
      onUpdate(project.id, projectResultUpdate(result));
    }
  }
  // Completions the SDK resolved on its own (tracked projects missing from the
  // snapshot) were replayed without the label: read each completed one's record
  // once. The list also names failed and canceled projects, whose stored
  // records answer 404 by design, so only completed tracked projects are read,
  // and one failed read never stops the rest of the pass.
  const labels = recoveredSensitiveLabels(synced);
  for (const id of (synced.completed || [])) {
    if (!wanted.has(id) || labels.has(id) || tracked.get(id)?.status !== 'completed') continue;
    let labelled = false;
    try {
      labelled = await lookupProjectSensitive(projects, id);
    } catch (error) {
      console.warn(`[Recovery] Sensitive label unavailable for ${id}:`, error?.message || error);
    }
    if (!isCurrent()) return;
    if (labelled) onUpdate(id, { sensitive: true });
  }
  const missing = [...wanted].filter((id) => !tracked.has(id) && !completed.has(id));
  if (!missing.length) return;
  const resolved = await projects.resolveMissing(missing, { attempts: 1, delayMs: 0 });
  if (!isCurrent()) return;
  for (const id of missing) {
    if (!isCurrent()) return;
    const resolution = resolved[id];
    if (resolution?.state === 'finished') {
      const result = await projects.getResult(id, { kind: 'image' });
      if (!isCurrent()) return;
      // The raw project the lookup returned carries the label the SDK's result entries drop.
      const update = projectResultUpdate(result);
      onUpdate(id, update.status === 'done' && rawProjectSensitive(resolution.project) ? { ...update, sensitive: true } : update);
    } else if (resolution?.state === 'terminal') {
      onUpdate(id, { status: resolution.project.status === 'canceled' ? 'canceled' : 'error',
        error: resolution.project.reason || 'Generation ended while you were away', unverified: false });
    } else if (resolution?.state === 'lost') {
      onUpdate(id, { status: 'unverified', error: 'This project could not be verified. Check existing projects or account creations before retrying.',
        errorCode: 'PROJECT_LOST', unverified: true });
    } else {
      // An unknown or active result without a tracked project is not proof of failure.
      onUpdate(id, { status: 'unverified', unverified: true,
        error: 'This project is still being verified. Check its status before retrying.' });
    }
  }
}

/** Keep original slot positions and selections when completed work is restored. */
export function mergeRecoveredIteration(session, run) {
  const request = run?.request;
  if (!request?.slots?.length) return;
  const slotIds = new Set(request.slots.map((slot) => slot.id));
  let iteration;
  for (const branch of session.branches || []) {
    iteration = branch.iterations.find((item) => item.request?.id === request.id || item.requestId === request.id ||
      item.batches?.some((batch) => batch?.some((image) => slotIds.has(image?.slotId))));
    if (iteration) break;
  }
  if (!iteration) {
    let branch = session.branches.find((item) => item.id === session.currentBranchId);
    if (!branch) {
      branch = { id: crypto.randomUUID(), createdAt: request.createdAt || Date.now(), iterations: [] };
      session.branches.push(branch);
      session.currentBranchId = branch.id;
    }
    iteration = { id: `recovered-${request.id}`, requestId: request.id, request,
      prompt: session.creativeBrief?.rawPrompt || request.slots[0].prompt,
      batches: [], refinementNotes: 'Recovered saved generation',
      timestamp: request.createdAt || Date.now(), userEdited: false };
    branch.iterations.push(iteration);
    session.currentIterationIndex = branch.iterations.length - 1;
  }
  iteration.request = request;
  for (const slot of request.slots) {
    const existing = iteration.batches?.[slot.batchIndex]?.[slot.imageIndex];
    const result = run.results[slot.id];
    // A verified URL refresh belongs to the same known project. Retained
    // native Blobs and successful tiles from other attempts stay untouched.
    if (existing?.status === 'done' && existing.url && !existing.blob &&
      result?.status === 'done' && result.unverified === false && result.url && existing.projectId &&
      existing.projectId === result.projectId && existing.url !== result.url) existing.url = result.url;
  }
  iteration.batches = mergeRequestBatches(request, { batches: iteration.batches, results: run.results });
  return iteration;
}
